/* Peakspan H2 — electrolysis cutaway.
 *
 * Honesty note, deliberately load-bearing: the number this counts up to is the
 * MANUFACTURER SPECIFICATION for the selected model, over that model's stated cycle
 * length. It is not a sensor reading and the markup says so. We have measured nothing.
 * If a real measurement ever exists, it replaces data-ppm and the label changes with it.
 *
 * Performance rules this file obeys:
 *   - does nothing until the section is near the viewport (IntersectionObserver)
 *   - never runs while the section is offscreen or the tab is hidden
 *   - device pixel ratio capped at 2
 *   - bubble count scales down on small screens
 *   - fully disabled under prefers-reduced-motion, leaving the static end state
 *   - animates canvas pixels and CSS transform/opacity only; never touches layout
 */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  function init(section) {
    var canvas = section.querySelector(".h2cut__bubbles");
    var ppmEl = section.querySelector("[data-ppm-value]");
    var steps = Array.prototype.slice.call(section.querySelectorAll(".h2cut__step"));
    var plate = section.querySelector(".h2cut__plate");
    var target = parseFloat(section.dataset.ppm || "0");
    var decimals = parseInt(section.dataset.ppmDecimals || "1", 10);

    // Static end state first, so the section is complete even if everything below bails.
    if (ppmEl) ppmEl.textContent = target.toFixed(decimals);
    steps.forEach(function (s, i) { s.dataset.active = i === steps.length - 1 ? "true" : "false"; });

    if (reduceMotion.matches || !canvas || !canvas.getContext) return;

    var ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    var bubbles = [];
    var running = false;
    var raf = 0;
    var progress = 0;
    var w = 0, h = 0;

    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      var count = w < 420 ? 26 : w < 700 ? 44 : 64;
      bubbles = [];
      for (var i = 0; i < count; i++) bubbles.push(spawn(true));
    }

    function spawn(scatter) {
      return {
        x: w * (0.30 + Math.random() * 0.40),
        y: scatter ? h * (0.42 + Math.random() * 0.46) : h * 0.88,
        r: 0.9 + Math.random() * 2.4,
        v: 0.18 + Math.random() * 0.55,
        drift: (Math.random() - 0.5) * 0.22,
        a: 0.25 + Math.random() * 0.5
      };
    }

    function frame() {
      if (!running) return;
      ctx.clearRect(0, 0, w, h);

      // Bubble density tracks how far through the cycle we are.
      var active = Math.max(0.15, progress);
      var shown = Math.floor(bubbles.length * active);

      for (var i = 0; i < shown; i++) {
        var b = bubbles[i];
        b.y -= b.v * (0.6 + active);
        b.x += b.drift;
        if (b.y < h * 0.40) {
          bubbles[i] = spawn(false);
          continue;
        }
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(52, 211, 153, " + b.a.toFixed(3) + ")";
        ctx.fill();
      }
      raf = window.requestAnimationFrame(frame);
    }

    function start() {
      if (running || reduceMotion.matches) return;
      running = true;
      if (!w) resize();
      raf = window.requestAnimationFrame(frame);
    }

    function stop() {
      running = false;
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
    }

    // Scroll drives the cycle: 0 when the section enters, 1 when it is centred.
    function onScroll() {
      var rect = section.getBoundingClientRect();
      var vh = window.innerHeight || 1;
      var p = 1 - (rect.top + rect.height * 0.35) / vh;
      progress = Math.min(1, Math.max(0, p));

      if (ppmEl) ppmEl.textContent = (target * progress).toFixed(decimals);
      if (plate) plate.classList.toggle("h2cut__plate--live", progress > 0.12);

      var idx = Math.min(steps.length - 1, Math.floor(progress * steps.length));
      steps.forEach(function (s, i) { s.dataset.active = i === idx ? "true" : "false"; });
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          start();
          window.addEventListener("scroll", onScroll, { passive: true });
          onScroll();
        } else {
          stop();
          window.removeEventListener("scroll", onScroll);
        }
      });
    }, { rootMargin: "200px 0px" });

    io.observe(section);

    window.addEventListener("resize", resize, { passive: true });
    document.addEventListener("visibilitychange", function () {
      if (document.hidden) stop(); else if (isNear()) start();
    });

    function isNear() {
      var r = section.getBoundingClientRect();
      return r.bottom > -200 && r.top < (window.innerHeight || 0) + 200;
    }

    // If the user flips reduced-motion on mid-session, honour it immediately.
    var onPref = function () {
      if (reduceMotion.matches) {
        stop();
        ctx.clearRect(0, 0, w, h);
        if (ppmEl) ppmEl.textContent = target.toFixed(decimals);
      }
    };
    if (reduceMotion.addEventListener) reduceMotion.addEventListener("change", onPref);

    resize();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-h2-cutaway]"), init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
